import type { LocationQuery, LocationQueryRaw } from 'vue-router'
import type { SortKey, StatusFilter } from './listView'

// What of the list view goes into the URL (`?status=all&sort=due&q=milk`), so it survives F5 and can be bookmarked or
// shared. Only what differs from the default is written; the grouping and the folded groups are per-device
// preferences and stay out of it.
export interface ListViewFields {
  status: StatusFilter
  sort: SortKey
  search: string
}

const STATUSES: readonly string[] = ['open', 'all', 'done']
const SORTS: readonly string[] = ['manual', 'due', 'priority', 'created', 'title']
const DEFAULTS: ListViewFields = { status: 'open', sort: 'manual', search: '' }

function single(value: LocationQuery[string] | undefined): string | null {
  return typeof value === 'string' ? value : null
}

// The valid parts of a route query; whatever is missing, unknown or repeated is left out.
export function readViewQuery(query: LocationQuery): Partial<ListViewFields> {
  const view: Partial<ListViewFields> = {}
  const status = single(query.status)
  if (status !== null && STATUSES.includes(status)) view.status = status as StatusFilter
  const sort = single(query.sort)
  if (sort !== null && SORTS.includes(sort)) view.sort = sort as SortKey
  const search = single(query.q)?.trim()
  if (search) view.search = search
  return view
}

// `query` with the view set in it, a key only when it is not the default; every other key stays as it was.
export function writeViewQuery(query: LocationQuery, view: ListViewFields): LocationQueryRaw {
  const next: LocationQueryRaw = { ...query }
  delete next.status
  delete next.sort
  delete next.q
  if (view.status !== DEFAULTS.status) next.status = view.status
  if (view.sort !== DEFAULTS.sort) next.sort = view.sort
  const search = view.search.trim()
  if (search) next.q = search
  return next
}

// The further tags of a tag page (`?tag=Work&tag=Garden`, next to the one in the path): trimmed, without blanks,
// duplicates (case-insensitive) and the tag of the page itself, in the order of the URL. Empty off a tag page.
export function readExtraTags(query: LocationQuery, pageTag: string | null): string[] {
  if (pageTag === null) return []
  const raw = query.tag === undefined ? [] : Array.isArray(query.tag) ? query.tag : [query.tag]
  const seen = new Set([pageTag.toLocaleLowerCase()])
  const tags: string[] = []
  for (const value of raw) {
    const tag = (value ?? '').trim()
    if (!tag || seen.has(tag.toLocaleLowerCase())) continue
    seen.add(tag.toLocaleLowerCase())
    tags.push(tag)
  }
  return tags
}

// `query` with the further tags set (or the key gone without any); every other key stays as it was.
export function withExtraTags(query: LocationQuery, tags: readonly string[]): LocationQueryRaw {
  const next: LocationQueryRaw = { ...query }
  delete next.tag
  if (tags.length) next.tag = [...tags]
  return next
}
